import {
    Chart as ChartJS,
    ChartData,
    ChartOptions,
    ChartType,
    ArcElement,
    TimeScale,
    LineController,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale
} from "chart.js";
import "chartjs-adapter-date-fns";
import { Line, Bar, Pie, Doughnut, Radar, PolarArea, Bubble, Scatter } from "react-chartjs-2";
import JSON5 from "json5";


export interface ChartConfig {
    type: ChartType;
    data: ChartData;
    options?: ChartOptions;
}


/**
 * Sanitizes Chart.js options by removing invalid callback properties.
 * JSON cannot serialize functions, so callbacks that come as strings will break Chart.js.
 * This function removes any non-function callbacks from the tooltip and other plugin configs.
 */
const sanitizeChartOptions = (options: any): any => {
    if (!options || typeof options !== "object") return options;

    const sanitized = { ...options };

    // Sanitize plugins.tooltip.callbacks - remove non-function callbacks
    if (sanitized.plugins?.tooltip?.callbacks) {
        const callbacks = sanitized.plugins.tooltip.callbacks;
        const validCallbacks: Record<string, any> = {};
        for (const key of Object.keys(callbacks)) {
            if (typeof callbacks[key] === "function") {
                validCallbacks[key] = callbacks[key];
            } else {
                // Skip non-function callbacks (strings, numbers, etc.) that would cause errors
                console.warn(`‹chrt_sanitize›: Removing invalid tooltip callback '${key}' (not a function)`);
            }
        }
        sanitized.plugins = {
            ...sanitized.plugins,
            tooltip: {
                ...sanitized.plugins.tooltip,
                callbacks: Object.keys(validCallbacks).length > 0 ? validCallbacks : undefined
            }
        };
    }

    // Sanitize plugins.legend.labels.generateLabels if present
    if (sanitized.plugins?.legend?.labels?.generateLabels && typeof sanitized.plugins.legend.labels.generateLabels !== "function") {
        console.warn("‹chrt_sanitize›: Removing invalid legend.labels.generateLabels (not a function)");
        sanitized.plugins.legend.labels = { ...sanitized.plugins.legend.labels };
        delete sanitized.plugins.legend.labels.generateLabels;
    }

    // Sanitize scales callbacks and time scale configurations
    if (sanitized.scales) {
        for (const scaleKey of Object.keys(sanitized.scales)) {
            const scale = sanitized.scales[scaleKey];
            if (scale?.ticks?.callback && typeof scale.ticks.callback !== "function") {
                console.warn(`‹chrt_sanitize›: Removing invalid scales.${scaleKey}.ticks.callback (not a function)`);
                sanitized.scales[scaleKey] = {
                    ...scale,
                    ticks: { ...scale.ticks }
                };
                delete sanitized.scales[scaleKey].ticks.callback;
            }

            // Fix time scale issues - remove problematic time configurations
            if (scale?.type === "time" || scale?.type === "timeseries") {
                sanitized.scales[scaleKey] = {
                    ...scale,
                    // Set bounds to 'data' to auto-calculate from actual data points
                    bounds: scale.bounds || "data",
                    // Remove min/max if they are 0 or invalid
                    ...(scale.min === 0 || scale.min === undefined ? {} : { min: scale.min }),
                    ...(scale.max === 0 || scale.max === undefined ? {} : { max: scale.max }),
                };
                // Remove explicit min/max if they are 0 (invalid)
                if (sanitized.scales[scaleKey].min === 0) delete sanitized.scales[scaleKey].min;
                if (sanitized.scales[scaleKey].max === 0) delete sanitized.scales[scaleKey].max;
            }
        }
    }

    return sanitized;
};

/**
 * Sanitizes chart data to remove invalid data points that could break time scales.
 * Filters out data points with 0, null, or undefined x/y values for time-based charts.
 */
const sanitizeChartData = (data: any, options: any): any => {
    if (!data || typeof data !== "object") return data;

    const sanitized = { ...data };

    // Check if any scale is a time scale
    const hasTimeScale = options?.scales && Object.values(options.scales).some(
        (scale: any) => scale?.type === "time" || scale?.type === "timeseries"
    );

    if (hasTimeScale && sanitized.datasets) {
        sanitized.datasets = sanitized.datasets.map((dataset: any) => {
            if (!dataset.data || !Array.isArray(dataset.data)) return dataset;

            const filteredData = dataset.data.filter((point: any) => {
                // Handle object format { x: timestamp, y: value }
                if (point && typeof point === "object" && "x" in point) {
                    const x = point.x;
                    // Filter out invalid x values (0, null, undefined, or non-positive numbers for timestamps)
                    if (x === 0 || x === null || x === undefined) {
                        console.warn("‹chrt_sanitize›: Filtering out data point with invalid x value:", point);
                        return false;
                    }
                    // If x is a number, ensure it's a valid timestamp (positive and reasonable)
                    if (typeof x === "number" && (x <= 0 || !Number.isFinite(x))) {
                        console.warn("‹chrt_sanitize›: Filtering out data point with invalid timestamp:", point);
                        return false;
                    }
                }
                return true;
            });

            return { ...dataset, data: filteredData };
        });
    }

    // Also filter labels if they contain invalid values
    if (hasTimeScale && sanitized.labels && Array.isArray(sanitized.labels)) {
        const validLabels: any[] = [];
        const validIndices: number[] = [];

        sanitized.labels.forEach((label: any, index: number) => {
            if (label !== 0 && label !== null && label !== undefined) {
                validLabels.push(label);
                validIndices.push(index);
            } else {
                console.warn("‹chrt_sanitize›: Filtering out invalid label at index", index, ":", label);
            }
        });

        // Only filter if some labels were removed
        if (validLabels.length < sanitized.labels.length) {
            sanitized.labels = validLabels;

            // Also filter corresponding data points in datasets
            if (sanitized.datasets) {
                sanitized.datasets = sanitized.datasets.map((dataset: any) => {
                    if (!dataset.data || !Array.isArray(dataset.data)) return dataset;

                    // Only filter if data is primitive array (not object format)
                    if (dataset.data.length > 0 && typeof dataset.data[0] !== "object") {
                        const filteredData = validIndices.map(i => dataset.data[i]);
                        return { ...dataset, data: filteredData };
                    }
                    return dataset;
                });
            }
        }
    }

    return sanitized;
};

export const parseChartConfig = (configJson: string) => {
    let config: ChartConfig;
    let configJ: any;
    try{
        configJ = JSON5.parse(configJson);
    }  catch {
        configJ = {};
        console.log("‹errr_chrt_prs_091›");
    }


    if (typeof configJ === "string"){
        try  {
            config = JSON5.parse(configJ) as ChartConfig;
        } catch {
            config = {} as ChartConfig;
            console.log("‹errr_chrt_prs_092›");
        }
    } else {
        if (configJ?.chartjs_json){
            try  {
                config = configJ.chartjs_json as ChartConfig;
            } catch {
                config = {} as ChartConfig;
                console.log("‹errr_chrt_prs_094›");
            }
        } else {
            config = configJ as ChartConfig;
        }
    }

    // Sanitize options to remove invalid callbacks that can't be serialized as JSON
    if (config.options) {
        config.options = sanitizeChartOptions(config.options);
    }

    // Sanitize data to remove invalid data points that break time scales
    if (config.data) {
        config.data = sanitizeChartData(config.data, config.options);
    }

    return config;
}

